import React from 'react';
import { useHistory } from 'react-router-dom';

import Cta from 'core/components/Cta';
import { useDropdown, useSens, useTrackEvent } from 'core/hooks';
import { ICONS } from 'core/components/Icon';
import * as Typo from 'core/styles/Typography';
import Image from 'core/sitecore/jss-proxy-components/JssImage';
import { DATE_FORMATS, convertDate } from 'core/utils/date-utils';
import { SITECORE_PAGES } from 'core/constants';

import * as S from './Notifications.style';
import { IFields as IHeaderFields } from '../../../Header.sitecore';

export interface IProps
	extends Pick<
		IHeaderFields,
		| 'level1NotificationButton'
		| 'notificationsEmptyStateMessage'
		| 'unreadNotificationLabel'
		| 'unreadNotificationsTitle'
		| 'seeAllNotificationsLink'
		| 'seeAllNotificationsLinkForMobile'
		| 'notificationsEmptyStateIllustration'
	> {
	dataTargetRoot?: string;
}

const Notifications = ({
	level1NotificationButton,
	unreadNotificationsTitle,
	seeAllNotificationsLink,
	notificationsEmptyStateIllustration,
	notificationsEmptyStateMessage,
	dataTargetRoot = 'desktop--header--notification',
}: IProps) => {
	const { unreadNotifications, unreadMsgCount } = useSens();
	const history = useHistory();

	// Analytics spreadsheet:
	// - Section: 'Header'
	const { trackEvent } = useTrackEvent(
		{
			event_section: 'navigation',
			event_type: 'header',
			event_title: 'authenticated',
		},
		false,
	);

	const NotificationsRef = React.useRef<HTMLDivElement>();
	const {
		Dropdown: NotificationsDropdown,
		isExpanded: isNotificationsExpanded,
		toggleDropdown: toggleNotificationsDropdown,
	} = useDropdown(NotificationsRef);

	const _handleViewDetails = (id: string, title?: string) => {
		const urlFromId = `${SITECORE_PAGES.MESSAGES_FROM_HEADER}?id=${id}`;
		history?.push(urlFromId);

		// Analytics spreadsheet:
		// - Section: 'Header'
		// - ID: 1.1.5
		trackEvent({
			event_trigger: 'click link',
			event_label: `notifications - ${title}`,
			event_url: urlFromId,
		});
	};

	return (
		<S.BtnWrapper ref={NotificationsRef as any}>
			<Cta
				ctaType="button"
				ctaStyle="plain"
				icon={ICONS.MAIL}
				iconAlign="left"
				onClick={toggleNotificationsDropdown}
				aria-expanded={isNotificationsExpanded}
				iconProps={{ size: 20, style: { right: '5px' } }}
				data-target-id={`${dataTargetRoot}--toggle-btn`}
				aria-label={!!unreadMsgCount ? 'view unread messages' : ''}
			>
				{/* Conditional render notification number pill */}
				{!!unreadMsgCount && <S.Pill>{unreadMsgCount}</S.Pill>}
				{level1NotificationButton.value}
			</Cta>
			<NotificationsDropdown topOffset={65}>
				<S.NotificationsContainer>
					<S.NotificationsTitleWrapper>
						<Typo.Custom
							tag="p"
							jss={unreadNotificationsTitle}
							style={S.notificationTitle}
							classNameIdentifier="notification--panel-title"
							data-target-id={`${dataTargetRoot}--panel-title`}
						/>
						<Cta
							ctaStyle="plain"
							isWithoutPadding
							onClick={() => {
								toggleNotificationsDropdown();
								// Analytics spreadsheet:
								// - Section: 'Header'
								// - ID: 1.1.5
								trackEvent({
									event_trigger: 'click link',
									event_label: 'notifications - see all notifications',
									event_url:
										seeAllNotificationsLink?.value?.href ??
										seeAllNotificationsLink?.value?.url,
								});
							}}
							jssLink={seeAllNotificationsLink}
							data-target-id={`${dataTargetRoot}--all-notifications-link`}
						/>
					</S.NotificationsTitleWrapper>
					{!!unreadMsgCount ? (
						<S.Content>
							{unreadNotifications?.map(notification => (
								<S.Tile key={notification.id}>
									<Cta
										ctaStyle="plain"
										isWithoutPadding
										jssText={{ value: notification.message_title }}
										data-target-id={`${dataTargetRoot}--notification-title-${notification.id}`}
										onClick={() => {
											_handleViewDetails(
												notification.id,
												notification?.message_title,
											);
										}}
									/>
									<Typo.Caption
										margin="0 0 16px"
										as="p"
										color="subdued"
										data-target-id={`${dataTargetRoot}--notification-date-${notification.id}`}
									>
										{notification?.communication_date &&
											convertDate(
												notification.communication_date,
												DATE_FORMATS.DAY_MONTH_NAME_YEAR_FNS,
											)}
									</Typo.Caption>
								</S.Tile>
							))}
						</S.Content>
					) : (
						<S.Content padding="20px 0 32px">
							<Image
								field={notificationsEmptyStateIllustration}
								data-target-id={`${dataTargetRoot}--no-notification-illustration`}
							/>
							<Typo.Body
								jss={notificationsEmptyStateMessage}
								data-target-id={`${dataTargetRoot}--no-notification-message`}
							/>
						</S.Content>
					)}
				</S.NotificationsContainer>
			</NotificationsDropdown>
		</S.BtnWrapper>
	);
};

export default Notifications;
